import { MONTHS, WEEKS, DAYS, labelMappings, VALID, INVALID, INVALID_RANGE_OVERLAPPING } from "../../../utils/utils";
import store from "../../../../../../src/stateManagement/store";
import { CouponTimingSlab } from "../../../types";
import dayjs, { Dayjs } from "dayjs";
import { addCouponTimingSlab } from "../../../../../stateManagement/actions/manageCouponActions";

type DateObject = {
    value: string;
    label: string;
};

const compareDates = (a: DateObject, b: DateObject): number => {
    const [day1, month1, year1] = a.value.split('/');
    const [day2, month2, year2] = b.value.split('/');

    // Create Date objects for comparison
    const dateObj1 = new Date(Number(year1), Number(month1), Number(day1));
    const dateObj2 = new Date(Number(year2), Number(month2), Number(day2));

    // Compare Date objects
    return dateObj1.getTime() - dateObj2.getTime();
};

/**
 * generated list of weeks from given months
 */

export const weeksCalculator = (months: number[], startDate: Date, endDate: Date): number[] => {
    if (months.length === 0) {
        let startMonth = startDate.getMonth()
        let endMonth = endDate.getMonth()
        const totalMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
            (endDate.getMonth() - startDate.getMonth()) + 1;
        months = Array.from({ length: totalMonths }, (_, index) => startMonth + index);
    }
    const weeksSet = new Set<number>();
    months.forEach(month => {
        // Get the last day of the month
        const lastDayOfMonth = new Date(startDate.getFullYear(), month + 1, 0);
        // Calculate the number of weeks in the month
        const weeksInMonth = Math.ceil((lastDayOfMonth.getDate() + lastDayOfMonth.getDay()) / 7);
        // Add each week to the set
        for (let week = 0; week <= weeksInMonth; week++) {
            weeksSet.add(week);
        }
    });
    return Array.from(weeksSet);
}

/**
 * generates list of dates in sorted order based on selected months and week
 */
const DAYS_IN_A_WEEK = 7;
export const generateDatesForWeeks = (selectedMonths: number[], selectedWeeks: number[], startDate: string, endDate: string): DateObject[] => {
    let years: number[] = [];
    const formattedStartDate = new Date(Date.parse(startDate));
    const formattedEndDate = new Date(Date.parse(endDate));

    if (selectedMonths.length === 0 && selectedWeeks.length === 0) {
        const startYear = formattedStartDate.getFullYear();
        const endYear = formattedEndDate.getFullYear();
        const totalYears = endYear - startYear + 1;
        const monthsSet = new Set<number>();
        for (let year = startYear; year <= endYear; year++) {
            const startMonth = (year === startYear) ? formattedStartDate.getMonth() : 0;
            const endMonth = (year === endYear) ? formattedEndDate.getMonth() : 11;
            for (let month = startMonth; month <= endMonth; month++) {
                monthsSet.add(month);
            }
        }
        selectedMonths = Array.from(monthsSet);
    }
    else if (selectedMonths.length === 0) {
        const startYear = formattedStartDate.getFullYear();
        const endYear = formattedEndDate.getFullYear();
        const totalYears = endYear - startYear + 1;
        const monthsSet = new Set<number>();
        for (let year = startYear; year <= endYear; year++) {
            const startMonth = (year === startYear) ? formattedStartDate.getMonth() : 0;
            const endMonth = (year === endYear) ? formattedEndDate.getMonth() : 11;
            for (let month = startMonth; month <= endMonth; month++) {
                monthsSet.add(month);
            }
        }
        selectedMonths = Array.from(monthsSet);
    }
    if (startDate !== undefined && startDate !== null && startDate !== "" && endDate !== undefined && endDate !== null && endDate !== "") {
        const startYear = formattedStartDate.getFullYear();
        const endYear = formattedEndDate.getFullYear();
        years = Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
    }
    else if (startDate !== undefined && startDate !== null && startDate !== "" && (endDate !== undefined || endDate !== null || endDate !== "")) {
        const formattedStartDate = new Date(Date.parse(startDate));
        const startYear = formattedStartDate.getFullYear();
        years = Array.from({ length: Number(new Date().getFullYear) - startYear + 1 }, (_, index) => startYear + index);
    }
    else if (endDate !== undefined && endDate !== null && endDate !== "" && (startDate !== undefined || startDate !== null || startDate !== "")) {
        const formattedEndDate = new Date(Date.parse(endDate));
        const endYear = formattedEndDate.getFullYear();
        years = Array.from({ length: Number(new Date().getFullYear) - endYear + 1 }, (_, index) => endYear + index);
    }
    // adding all to the weeks
    const currentDate = new Date();
    const datesForWeeks: DateObject[] = [];
    selectedMonths.sort((a, b) => a - b);
    selectedWeeks.sort((a, b) => a - b);
    years.forEach((year) => {
        selectedMonths.forEach((selectedMonth) => {
            const firstDayOfMonth = new Date(year, selectedMonth, 1);
            const dayOfWeekOfFirstDay = firstDayOfMonth.getDay();
            if (selectedWeeks.length === 0) {
                const lastDayOfMonth = new Date(year, selectedMonth + 1, 0);
                const weeks = Math.ceil(lastDayOfMonth.getDate() / 7);
                selectedWeeks = Array.from(Array(weeks + 1).keys());
            }
            selectedWeeks.forEach((selectedWeek) => {
                const startDay = selectedWeek * DAYS_IN_A_WEEK - dayOfWeekOfFirstDay + 1;
                const datesForWeek: DateObject[] = [];
                for (let dateOfWeek = 0; dateOfWeek < DAYS_IN_A_WEEK; dateOfWeek++) {
                    const date = new Date(year, selectedMonth, startDay + dateOfWeek);
                    const formattedDate = date.toLocaleDateString("en-GB");
                    if (date.getMonth() === selectedMonth) {
                        datesForWeek.push({ value: formattedDate, label: formattedDate });
                    }
                }
                datesForWeeks.push(...datesForWeek);
            });
        });
    });

    // const sortedDates = datesForWeeks.toSorted((a, b) => {});
    const filteredDates = datesForWeeks.filter(dateString => {
        const dateParts = dateString.value.split('/');
        const year = parseInt(dateParts[2]);
        const month = parseInt(dateParts[1]) - 1; // Month is zero-based
        const day = parseInt(dateParts[0]);
        const date = new Date(year, month, day);

        // Check if date is within the range of startDate and endDate
        return formattedStartDate <= date && date <= formattedEndDate;
    });
    const sortedDates = filteredDates.sort(compareDates);

    return sortedDates;
};

/**
 * 
 * @returns returns a map of month to is coresponding index starting fromc 0;
 */
export const monthIndexMapper = (): Record<string, number> => {
    const monthIndexMap: Record<string, number> = {};
    MONTHS.forEach((month, index) => {
        monthIndexMap[month.value] = index;
    });
    return monthIndexMap;
}


/**
 * 
 * @returns returns a map of week to is coresponding index starting fromc 0;
 */
export const weekIndexMapper = (): Record<string, number> => {
    const weekIndexMap: Record<string, number> = {};
    WEEKS.forEach((week, index) => {
        weekIndexMap[week.value] = index;
    });
    return weekIndexMap;
}


/**
 * 
 * @returns returns a map of day to is coresponding index starting fromc 0;
 */

export const dayIndexMapper = (): Record<string, number> => {
    const dayIndexMap: Record<string, number> = {};
    DAYS.forEach((day, index) => {
        dayIndexMap[day.value] = index;
    });
    return dayIndexMap;
}


/**
 * 
 * @returns returns a map of index to is coresponding month starting fromc 0;
 */
export const indexMonthMapper = (): Record<number, string> => {

    const indexMonthMap: Record<number, string> = {};
    MONTHS.forEach((month, index) => {
        indexMonthMap[index] = month.value;
    });
    return indexMonthMap;
}

/**
 * 
 * @returns returns a map of index to is coresponding week starting fromc 0;
 */
export const indexWeekMapper = (): Record<number, string> => {

    const indexWeekMap: Record<number, string> = {};
    WEEKS.forEach((week, index) => {
        indexWeekMap[index] = week.value;
    });
    return indexWeekMap;
}

/**
 * 
 * @returns returns a map of index to is coresponding day starting fromc 0;
 */

export const indexDayMapper = (): Record<number, string> => {
    const indexDayMap: Record<number, string> = {};
    DAYS.forEach((day, index) => {
        indexDayMap[index] = day.value;
    });
    return indexDayMap;
}




export const handleRecurrenceUnsavedData = (): string => {

    const res = saveUnsavedCoupnTimingSlabs();
    
    if(res.trim() !== "" ){
        return res;
    }

    const { activePeriodCustomDates, activePeriodDays, activePeriodWeeks, activePeriodMonths, nDaysFromActivation, isActivePeriodSaved ,skipNthOrder,numberOfPurchases , orderResetFrequency} = store.getState().manageCoupons?.data?.recurrence;
    const { couponEditType } = store.getState().manageCoupons?.data;

   
    if (couponEditType === "edit" || couponEditType === "clone") {
        return "";
    }
    if (
        ((activePeriodCustomDates && activePeriodCustomDates.length !== 0) ||
            (activePeriodDays && activePeriodDays.length !== 0) ||
            (activePeriodWeeks && activePeriodWeeks.length !== 0) ||
            (activePeriodMonths && activePeriodMonths.length !== 0) ||
            (nDaysFromActivation && nDaysFromActivation !== "")
        ) &&
        ((isActivePeriodSaved !== null || isActivePeriodSaved !== undefined) && isActivePeriodSaved === false)
    ) {
        return labelMappings["RECURRENCE_NOT_SAVED"];
    }
    if (!skipNthOrder && (numberOfPurchases === "" || orderResetFrequency === "" || orderResetFrequency === null)) {
        return labelMappings["EMPTY_NTH_PURCHASE_ERROR"]
    }
    if (!skipNthOrder && (Number(numberOfPurchases) <= 1)) {
        return labelMappings["NTH_PURCHASE_GREATER_THAN_1_Alert"]
    }
    return "";
}

export const isAnyRecurrenceOptionPresent = () => {
    const { activePeriodCustomDates, activePeriodDays, activePeriodWeeks, activePeriodMonths, nDaysFromActivation, savedCalendar, usabilityLimit, usability, orderResetFrequency, couponLife, numberOfPurchases } = store.getState().manageCoupons?.data?.recurrence;

    const shouldNotBeEmptyArray = [activePeriodCustomDates, activePeriodDays, activePeriodWeeks, activePeriodMonths];
    const shouldNotBeNullAndEmptyString = [nDaysFromActivation, usabilityLimit, usability, orderResetFrequency, numberOfPurchases, couponLife];

    return shouldNotBeEmptyArray.some((item) => item.length > 0) || shouldNotBeNullAndEmptyString.some((item: any) => item !== null && item !== "");
}

const saveUnsavedCoupnTimingSlabs =()=>{
    const { couponTimings }:{couponTimings:Array<CouponTimingSlab>} = store.getState().manageCoupons?.data?.recurrence;

    for(let timing of couponTimings){
        const {st , et , isSaved} = timing;
        
        if (isSaved || (st === null && et === null)) continue;

        const validationErrors = validateSingleSabCouponTiming({st,et},timing);
        const res = isCouponTimingsSlabValid(validationErrors,timing);
        
        if(res === INVALID) return labelMappings["COUPON_TIMINGS_SLAB_VALIDATION_FAILED"];
        else if( res === INVALID_RANGE_OVERLAPPING) return labelMappings["COUPON_TIMING_SLAB_RANGE_OVERLAPPING"];
        
        addCouponTimingSlab({...timing , isSaved:true},timing.id)
    
    }

    return "";

}

/**
 * Empty Data strucuture of Coupon Timing Data
 * @returns 
 */
export const getEmptyTimingSlab = ()=>{
    return {
        st:null,
        et:null,   
    }
}


export const isEmptySlabPresentInCouponTimigs = (data:Record<string,string>[])=>{
    return data.some((slab)=>slab?.st === null  && slab.et === null);
}


export const isCouponTimingsSlabValid = (errors:any , currentSlabValues:CouponTimingSlab)=>{
    const res1 = slabsFieldsValidation(errors , currentSlabValues);
    if(res1 !== VALID) return res1;

    const res2 = overlappingRangesInCouponTimingsSlab(currentSlabValues);
    if(res2 !==VALID) return res2;

    return VALID;
}


const slabsFieldsValidation = (errors :any, currentSlabValues:CouponTimingSlab)=>{
    if(currentSlabValues.st === null || currentSlabValues.et === null){
        return INVALID;
    }
    if(!Object.values(errors).every((x) => x === "")){
        return INVALID;
    }
    else return VALID;
}

const overlappingRangesInCouponTimingsSlab = (currentData:CouponTimingSlab)=>{
    const { couponTimings }: { couponTimings: Array<CouponTimingSlab> } = store.getState()?.manageCoupons?.data?.recurrence;
    for (let timings of couponTimings) {
        if (timings.id === currentData.id || !timings.isSaved) continue;
        else if ((timings["st"] && (timings["st"] > currentData["et"])) || (timings["et"] && (timings["et"] < currentData["st"])))
            continue;
        else return INVALID_RANGE_OVERLAPPING;
    }

    return VALID;
}

export const validateSingleSlabSave = (fieldValues:Record<string,Date>,currentSlabValues:CouponTimingSlab)=>{
    let newErrors:Record<string,string> = { };
    const {st , et} = currentSlabValues;
    if("st" in fieldValues){
        newErrors['st'] = fieldValues["st"] === null? 'Start Time is required':'';
    }
    if("et" in fieldValues){
        newErrors['et'] = fieldValues["et"] === null ? 'End Time is required' : '';
    }
    return newErrors;
}

export const validateSingleSabCouponTiming = (fieldValues:Record<string,Date>,currentSlabValues:CouponTimingSlab)=>{
    let newErrors:Record<string,string> = { };
    const {st , et} = currentSlabValues;

    if("st" in fieldValues && "et" in fieldValues) {
        if(fieldValues["st"] === null && fieldValues["et"] !== null) {
            return {};
        }
    }

    if("st" in fieldValues){
        newErrors['st'] = fieldValues["st"] === null? '':dayjs(fieldValues["st"]).isValid() ? '':'Invalid Start time';
    }
    if("et" in fieldValues){
        newErrors['et'] = fieldValues["et"] === null? '':dayjs(fieldValues["et"]).isValid() ? '':'Invalid End time';
    }


    if("st" in fieldValues &&  dayjs(fieldValues['st']).isValid() && dayjs(et).isValid()){
        newErrors['st'] = fieldValues["st"] > et ? 'Start Time should be less than End Time' : '';
        newErrors['et'] = fieldValues["et"] > et ? 'End Time should be greater than Start Time' : '';
    }
    if("et" in fieldValues &&  dayjs(fieldValues['et']).isValid() && dayjs(st).isValid()){
        newErrors['st'] = fieldValues["et"] < st ? 'Start Time should be less than End Time' : '';
        newErrors['et'] = fieldValues["et"] < st ? 'End Time should be greater than Start Time' : '';
    }

    return newErrors;
}

/**
 * Convert the timing used in coupon timeing DAYjs fromat to HH:mm format
 * @param time 
 * @returns 
 */
export function convertCouponTimingToString(time:Dayjs){
    return time.format("HH:mm");
}

/**
 * Convert the time saved ("HH:mm") in db back to coupon timing format dayjs
 * @param time 
 * @returns 
 */
export function convertTimeStringToCouponTiming(time:string){
    const timeArr:string[] = time.split(":");
    const d = new Date();
    d.setHours(parseInt(timeArr[0]));
    d.setMinutes(parseInt(timeArr[1]));
    return d;
}